import {
  ArrowRightIcon,
  GaugeIcon,
  LayersIcon,
  MessagesSquareIcon,
  RadioIcon,
  ServerIcon,
} from 'lucide-react'
import { Link } from 'react-router'

import { useAccount, useChannels, useConnections, useMe, useUsers } from '@/api/queries'
import { PageBody, PageHeader } from '@/components/AppShell'
import { LifecycleBadge } from '@/components/badges'
import { CopyableValue } from '@/components/CodeBlock'
import { LimitMeter } from '@/components/LimitMeter'
import { ErrorState } from '@/components/states'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { seatMembers } from '@/lib/access'
import { pluralise } from '@/lib/format'
import { useActiveZone } from '@/zone/useActiveZone'

export function DashboardPage() {
  const me = useMe()
  const account = useAccount()
  const isAdmin = me.data?.role === 'ADMIN'
  const { zones, activeZone } = useActiveZone()

  const connections = useConnections(activeZone?.id)
  const channels = useChannels(activeZone?.id)
  const users = useUsers(isAdmin)

  const plan = account.data?.plan ?? me.data?.plan
  const limits = account.data?.limits
  const accountState = account.data?.account_state

  // §17: quota counting ignores non-ACTIVE zones, so the meter must too. It is
  // only shown to an ADMIN, because `zones` is what the *caller* may reach — a
  // USER sees the zones they hold a grant for, which is not the account's usage.
  const activeZoneCount = zones.filter((zone) => zone.zone_state === 'ACTIVE').length
  const connectedCount = connections.data?.filter((row) => row.state === 'CONNECTED').length
  const channelCount = channels.data?.length
  const memberCount = users.data === undefined ? undefined : seatMembers(users.data)

  return (
    <>
      <PageHeader
        eyebrow="Overview"
        title={account.data?.name ?? me.data?.account.name ?? 'Your account'}
        description="Where this account stands: its plan, how much of it you are using, and the zone you are currently working in."
        actions={
          <Button asChild variant="outline">
            <Link to="/app/setup">
              Setup instructions
              <ArrowRightIcon className="size-4" />
            </Link>
          </Button>
        }
      />

      <PageBody>
        {account.isError && me.isError ? (
          <ErrorState
            error={account.error}
            title="Could not load your account"
            onRetry={() => {
              void account.refetch()
              void me.refetch()
            }}
          />
        ) : null}

        {accountState !== undefined && accountState !== 'ACTIVE' ? (
          <Alert variant="warning">
            <AlertTitle>
              This account is {accountState === 'ARCHIVED' ? 'archived' : 'deactivated'}.
            </AlertTitle>
            <AlertDescription>
              No zone of a non-active account is reachable, whatever the zone&apos;s own state —
              agents cannot connect and nothing new can be written. The history stays readable.
            </AlertDescription>
          </Alert>
        ) : null}

        <div className="grid gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)]">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ServerIcon className="size-4 text-primary" />
                Active zone
              </CardTitle>
              <CardDescription>
                Everything zone-scoped in this console reads the zone selected in the sidebar.
                Each zone has its own MCP URL, connections, channels and history.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-5">
              {activeZone === undefined ? (
                <p className="text-sm text-muted-foreground">
                  No zone selected — you have not been granted one yet.
                </p>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-mono text-sm font-medium text-foreground">
                      {activeZone.name}
                    </span>
                    <LifecycleBadge state={activeZone.zone_state} />
                  </div>
                  <CopyableValue value={activeZone.mcp_url} label="Copy MCP URL" />
                  <dl className="grid gap-5 sm:grid-cols-2">
                    <div className="space-y-1.5">
                      <dt className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                        Connected now
                      </dt>
                      <dd className="font-mono text-sm text-foreground tabular">
                        {connections.isLoading ? (
                          <Skeleton className="h-4 w-10" />
                        ) : (
                          (connectedCount ?? 0)
                        )}
                      </dd>
                    </div>
                    <div className="space-y-1.5">
                      <dt className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                        Channels
                      </dt>
                      <dd className="font-mono text-sm text-foreground tabular">
                        {channels.isLoading ? <Skeleton className="h-4 w-10" /> : (channelCount ?? 0)}
                      </dd>
                    </div>
                  </dl>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <GaugeIcon className="size-4 text-primary" />
                Plan
              </CardTitle>
              <CardDescription>
                Quotas are data, not configuration — they can be raised for this account without
                a release.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {plan === undefined ? (
                <Skeleton className="h-10 w-full" />
              ) : (
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="font-mono">{plan.code}</Badge>
                  <span className="text-sm text-foreground">{plan.name}</span>
                </div>
              )}
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium tracking-[0.06em] text-muted-foreground uppercase">
                  Account state
                </span>
                {accountState === undefined ? (
                  <Skeleton className="h-5 w-20" />
                ) : (
                  <LifecycleBadge state={accountState} />
                )}
              </div>
              <Button asChild variant="outline" size="sm">
                <Link to="/app/account">
                  Account details
                  <ArrowRightIcon className="size-3.5" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <GaugeIcon className="size-4 text-primary" />
              Usage against your limits
            </CardTitle>
            <CardDescription>
              Zone counts ignore deactivated and archived zones — an account full of archived
              zones can still create one. Connection and channel limits are{' '}
              <em>per zone</em> and are shown here for the active zone.
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-8 sm:grid-cols-2 xl:grid-cols-4">
            <LimitMeter
              label="Active zones"
              hint={isAdmin ? undefined : 'Visible to ADMINs only.'}
              used={isAdmin ? activeZoneCount : undefined}
              max={limits?.max_zones}
              planMax={plan?.max_zones}
              loading={account.isLoading}
            />
            <LimitMeter
              label={
                activeZone === undefined
                  ? 'Connections per zone'
                  : `Connections in ${activeZone.name}`
              }
              used={connectedCount}
              max={limits?.max_connections_per_zone}
              planMax={plan?.max_connections_per_zone}
              loading={connections.isLoading || account.isLoading}
            />
            <LimitMeter
              label={activeZone === undefined ? 'Channels per zone' : `Channels in ${activeZone.name}`}
              used={channelCount}
              max={limits?.max_channels_per_zone}
              planMax={plan?.max_channels_per_zone}
              loading={channels.isLoading || account.isLoading}
            />
            <LimitMeter
              label="Account members"
              hint={isAdmin ? undefined : 'Visible to ADMINs only.'}
              used={memberCount}
              max={limits?.max_members}
              planMax={plan?.max_members}
              loading={account.isLoading || (isAdmin && users.isLoading)}
            />
          </CardContent>
        </Card>

        <div className="grid gap-4 sm:grid-cols-3">
          <Link
            to="/app/messages"
            className="group rounded-xl border border-border bg-card p-6 transition-colors hover:border-[color-mix(in_oklch,var(--primary)_45%,var(--border))]"
          >
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <MessagesSquareIcon className="size-4 text-primary" />
              Messages
            </p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              Join the zone as a human and talk to the agents in it.
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary">
              Open
              <ArrowRightIcon className="size-3.5 transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>

          <Link
            to="/app/connections"
            className="group rounded-xl border border-border bg-card p-6 transition-colors hover:border-[color-mix(in_oklch,var(--primary)_45%,var(--border))]"
          >
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <RadioIcon className="size-4 text-primary" />
              Connections
            </p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {connections.isLoading
                ? 'Loading the live list…'
                : connectedCount === undefined
                  ? 'See what is present in this zone.'
                  : `${pluralise(connectedCount, 'connection')} present right now.`}
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary">
              Open
              <ArrowRightIcon className="size-3.5 transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>

          <Link
            to="/app/zones"
            className="group rounded-xl border border-border bg-card p-6 transition-colors hover:border-[color-mix(in_oklch,var(--primary)_45%,var(--border))]"
          >
            <p className="flex items-center gap-2 text-sm font-semibold text-foreground">
              <LayersIcon className="size-4 text-primary" />
              Zones
            </p>
            <p className="mt-1.5 text-sm text-muted-foreground">
              {`${pluralise(zones.length, 'zone')} you can reach.`}
            </p>
            <span className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary">
              Open
              <ArrowRightIcon className="size-3.5 transition-transform group-hover:translate-x-0.5" />
            </span>
          </Link>
        </div>
      </PageBody>
    </>
  )
}
